
import { useState, FormEvent } from 'react';

interface WriteReviewFormProps {
  businessName: string;
}

const FORM_URL = 'https://readdy.ai/api/form/d6abn7ea728k8ctu37s0';

export default function WriteReviewForm({ businessName }: WriteReviewFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [review, setReview] = useState('');
  const [recommend, setRecommend] = useState<string>('yes');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const maxChars = 500;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (rating === 0) {
      setError('Please select a star rating.');
      return;
    }
    if (!name.trim() || !email.trim() || !review.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    if (review.length > maxChars) {
      setError(`Review must be ${maxChars} characters or less.`);
      return;
    }

    setSubmitting(true);

    const formData = new URLSearchParams();
    formData.append('business', businessName);
    formData.append('rating', String(rating));
    formData.append('name', name.trim());
    formData.append('email', email.trim());
    formData.append('title', title.trim());
    formData.append('review', review.trim());
    formData.append('recommend', recommend);

    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setRating(0);
    setHoverRating(0);
    setName('');
    setEmail('');
    setTitle('');
    setReview('');
    setRecommend('yes');
    setSubmitted(false);
    setError('');
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full py-3 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-edit-line text-sm"></i>
        </div>
        Write a Review
      </button>
    );
  }

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
        <div className="text-center py-6">
          <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-emerald-600 text-2xl"></i>
          </div>
          <h4 className="text-lg font-bold text-gray-900 mb-2">Thank You!</h4>
          <p className="text-sm text-gray-600 leading-relaxed mb-1">
            Your review for <strong>{businessName}</strong> has been submitted.
          </p>
          <p className="text-xs text-gray-400 mb-5">
            It will appear after a brief moderation review.
          </p>
          <button
            onClick={resetForm}
            className="px-5 py-2.5 text-sm font-semibold text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <h4 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-edit-line text-teal-600"></i>
          </div>
          Write a Review
        </h4>
        <button
          onClick={resetForm}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-gray-400 text-lg"></i>
        </button>
      </div>

      <form
        id="write-review-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {/* Hidden business name */}
        <input type="hidden" name="business" value={businessName} />

        {/* Star Rating */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Your Rating <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="cursor-pointer p-0.5 transition-transform hover:scale-110"
              >
                <i
                  className={`text-2xl ${
                    star <= (hoverRating || rating)
                      ? 'ri-star-fill text-amber-400'
                      : 'ri-star-line text-gray-300'
                  } transition-colors`}
                ></i>
              </button>
            ))}
            {rating > 0 && (
              <span className="ml-2 text-sm text-gray-500">
                {rating === 1
                  ? 'Poor'
                  : rating === 2
                  ? 'Fair'
                  : rating === 3
                  ? 'Good'
                  : rating === 4
                  ? 'Very Good'
                  : 'Excellent'}
              </span>
            )}
          </div>
        </div>

        {/* Name & Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="reviewer-name" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Your Name <span className="text-red-500">*</span>
            </label>
            <input
              id="reviewer-name"
              name="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John D."
              required
              className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
            />
          </div>
          <div>
            <label htmlFor="reviewer-email" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="reviewer-email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@email.com"
              required
              className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
            />
            <p className="text-[11px] text-gray-400 mt-1">Not displayed publicly</p>
          </div>
        </div>

        {/* Review Title */}
        <div>
          <label htmlFor="review-title" className="block text-sm font-semibold text-gray-700 mb-1.5">
            Review Title
          </label>
          <input
            id="review-title"
            name="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Summarize your experience"
            className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
          />
        </div>

        {/* Review Body */}
        <div>
          <label htmlFor="review-body" className="block text-sm font-semibold text-gray-700 mb-1.5">
            Your Review <span className="text-red-500">*</span>
          </label>
          <textarea
            id="review-body"
            name="review"
            value={review}
            onChange={(e) => {
              if (e.target.value.length <= maxChars) {
                setReview(e.target.value);
              }
            }}
            placeholder="Share details about your experience — what did you like or dislike?"
            rows={4}
            required
            maxLength={maxChars}
            className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors resize-none"
          ></textarea>
          <div className="flex items-center justify-between mt-1">
            <p className="text-[11px] text-gray-400">Be specific and helpful for other visitors</p>
            <span
              className={`text-[11px] ${
                review.length > maxChars * 0.9 ? 'text-red-500' : 'text-gray-400'
              }`}
            >
              {review.length}/{maxChars}
            </span>
          </div>
        </div>

        {/* Recommend */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Would you recommend this business?
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="recommend"
                value="yes"
                checked={recommend === 'yes'}
                onChange={() => setRecommend('yes')}
                className="w-4 h-4 text-teal-600 accent-teal-600"
              />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="recommend"
                value="no"
                checked={recommend === 'no'}
                onChange={() => setRecommend('no')}
                className="w-4 h-4 text-teal-600 accent-teal-600"
              />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 px-3.5 py-2.5 bg-red-50 border border-red-200 rounded-lg">
            <i className="ri-error-warning-line text-red-500 text-sm"></i>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-sm"></i>
              Submitting...
            </>
          ) : (
            <>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-send-plane-line text-sm"></i>
              </div>
              Submit Review
            </>
          )}
        </button>

        <p className="text-[11px] text-gray-400 text-center leading-relaxed">
          By submitting, you agree that your review is based on a genuine experience. Reviews are moderated before publishing.
        </p>
      </form>
    </div>
  );
}
